import { Pipe, PipeTransform } from '@angular/core';
import { CurrencyService } from '../services/currency.service';

/**
 * Formats a numeric amount with the studio's currency symbol.
 *
 *   {{ amount | studioCurrency }}        => '€1,234.50' (Italian studio) / '$1,234.50' (US studio)
 *   {{ amount | studioCurrency:0 }}      => '€1,235'    (no decimals)
 *
 * Use this in place of a hardcoded `$` so money displays in the studio's currency.
 *
 * Impure because the studio's currency resolves asynchronously after config
 * loads; an impure pipe re-evaluates on each change-detection pass so the symbol
 * appears once it's known. The work per call is trivial (string formatting).
 */
@Pipe({
  name: 'studioCurrency',
  pure: false
})
export class StudioCurrencyPipe implements PipeTransform {
  constructor(private currencyService: CurrencyService) {}

  transform(value: number | string | null | undefined, decimals: number = 2): string {
    return this.currencyService.format(value, decimals);
  }
}

/**
 * Outputs just the studio's currency symbol, with no amount.
 *
 *   <span matPrefix>{{ '' | studioCurrencySymbol }}&nbsp;</span>   => '€' / '$'
 *
 * Use this for input-field prefixes and other places that show a bare currency
 * symbol (where a hardcoded `$` used to live). The piped value is ignored. The
 * symbol comes from the studio config, so any input works (`'' | ...`).
 *
 * Impure for the same reason as StudioCurrencyPipe: the symbol resolves once
 * studio config loads.
 */
@Pipe({
  name: 'studioCurrencySymbol',
  pure: false
})
export class StudioCurrencySymbolPipe implements PipeTransform {
  constructor(private currencyService: CurrencyService) {}

  transform(_value?: unknown): string {
    return this.currencyService.getSymbol();
  }
}
